import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { gql } from "@/gql";
import {
  CreateTrafficSourceInput,
  TrafficSourceFilterInput,
  UpdateTrafficSourceInput,
} from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";

const GET_TRAFFIC_SOURCES = gql(`
  query GetTrafficSources($salonId: ID!, $filter: TrafficSourceFilterInput!) {
    trafficSources(salonId: $salonId, filter: $filter) {
      edges {
        node {
          id
          code
          name
          description
          isActive
          isArchived
          sortOrder
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

const CREATE_TRAFFIC_SOURCE = gql(`
  mutation CreateTrafficSource($salonId: ID!, $input: CreateTrafficSourceInput!) {
    createTrafficSource(salonId: $salonId, input: $input) {
      id
      code
      name
      description
      isActive
      isArchived
      sortOrder
      createdAt
      updatedAt
    }
  }
`);

const UPDATE_TRAFFIC_SOURCE = gql(`
  mutation UpdateTrafficSource($id: ID!, $salonId: ID!, $input: UpdateTrafficSourceInput!) {
    updateTrafficSource(id: $id, salonId: $salonId, input: $input) {
      id
      code
      name
      description
      isActive
      isArchived
      sortOrder
      createdAt
      updatedAt
    }
  }
`);

const ARCHIVE_TRAFFIC_SOURCE = gql(`
  mutation ArchiveTrafficSource($id: ID!, $salonId: ID!, $archive: Boolean!) {
    archiveTrafficSource(id: $id, salonId: $salonId, archive: $archive) {
      id
      code
      name
      description
      isActive
      isArchived
      sortOrder
      createdAt
      updatedAt
    }
  }
`);

export function useTrafficSources() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const filter: TrafficSourceFilterInput = {
    first: 50,
    sortBy: "sortOrder",
    sortOrder: "asc",
  };

  const { data, loading, error, refetch } = useQuery(GET_TRAFFIC_SOURCES, {
    variables: {
      salonId: salonId ?? "",
      filter,
    },
    skip: !salonId,
  });

  const [createTrafficSourceMutation] = useMutation(CREATE_TRAFFIC_SOURCE);
  const [updateTrafficSourceMutation] = useMutation(UPDATE_TRAFFIC_SOURCE);
  const [archiveTrafficSourceMutation] = useMutation(ARCHIVE_TRAFFIC_SOURCE);

  const createTrafficSource = useCallback(
    async (input: CreateTrafficSourceInput) => {
      if (!salonId) {
        toast.error("No salon selected");
        return;
      }

      setIsCreating(true);
      try {
        const result = await createTrafficSourceMutation({
          variables: {
            salonId,
            input,
          },
        });

        toast.success("Traffic source created successfully");
        await refetch();
        return result.data?.createTrafficSource;
      } catch (error) {
        console.error("Create traffic source error:", error);
        toast.error("Failed to create traffic source");
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [salonId, createTrafficSourceMutation, refetch]
  );

  const updateTrafficSource = useCallback(
    async (id: string, input: UpdateTrafficSourceInput) => {
      if (!salonId) {
        toast.error("No salon selected");
        return;
      }

      setIsUpdating(true);
      try {
        const result = await updateTrafficSourceMutation({
          variables: {
            id,
            salonId,
            input,
          },
        });

        toast.success("Traffic source updated successfully");
        await refetch();
        return result.data?.updateTrafficSource;
      } catch (error) {
        console.error("Update traffic source error:", error);
        toast.error("Failed to update traffic source");
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [salonId, updateTrafficSourceMutation, refetch]
  );

  const archiveTrafficSource = useCallback(
    async (id: string, archive = true) => {
      if (!salonId) {
        toast.error("No salon selected");
        return;
      }

      setIsArchiving(true);
      try {
        const result = await archiveTrafficSourceMutation({
          variables: {
            id,
            salonId,
            archive,
          },
        });

        toast.success(archive ? "Traffic source archived" : "Traffic source restored");
        await refetch();
        return result.data?.archiveTrafficSource;
      } catch (error) {
        console.error("Archive traffic source error:", error);
        toast.error(`Failed to ${archive ? "archive" : "restore"} traffic source`);
        throw error;
      } finally {
        setIsArchiving(false);
      }
    },
    [salonId, archiveTrafficSourceMutation, refetch]
  );

  const trafficSources = data?.trafficSources?.edges?.map((edge) => edge.node) || [];
  const pageInfo = data?.trafficSources?.pageInfo;

  return {
    trafficSources,
    pageInfo,
    loading,
    error,
    isCreating,
    isUpdating,
    isArchiving,
    refetch,
    createTrafficSource,
    updateTrafficSource,
    archiveTrafficSource,
  };
}
